import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import {
  Button,
  Typography,
  Box,
  Alert,
  CircularProgress,
  Paper,
  styled
} from '@mui/material';
import { CheckCircle, Error, Email } from '@mui/icons-material';
import { apiFetch } from '../utils/api';
import { useAuth } from '../contexts/AuthContext';

const Container = styled(Box)(({ theme }) => ({
  minHeight: '100vh',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  background: theme.palette.mode === 'dark'
    ? 'linear-gradient(135deg, #1a1a2e 0%, #16213e 100%)'
    : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
  padding: '20px',
}));

const Card = styled(Paper)(({ theme }) => ({
  background: theme.palette.mode === 'dark' ? theme.palette.grey[900] : 'white',
  borderRadius: '16px',
  padding: '40px',
  boxShadow: theme.palette.mode === 'dark'
    ? '0 20px 40px rgba(0, 0, 0, 0.3)'
    : '0 20px 40px rgba(0, 0, 0, 0.1)',
  maxWidth: '500px',
  width: '100%',
  textAlign: 'center',
}));

const IconContainer = styled(Box)<{ success?: boolean; error?: boolean }>(({ success, error }) => ({
  width: '80px',
  height: '80px',
  borderRadius: '50%',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  margin: '0 auto 24px',
  background: success ? '#4caf50' : error ? '#f44336' : '#2196f3',
  color: 'white',
}));

const ActivateAccountPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { login } = useAuth();
  const [status, setStatus] = useState<'loading' | 'success' | 'error' | 'expired'>('loading');
  const [message, setMessage] = useState('');
  const [email, setEmail] = useState('');
  const [organizationName, setOrganizationName] = useState('');

  useEffect(() => {
    const token = searchParams.get('token')?.trim();
    const emailParam = searchParams.get('email');
    const orgParam = searchParams.get('organization');

    if (emailParam) {
      setEmail(emailParam);
    }

    if (orgParam) {
      setOrganizationName(orgParam);
    }

    if (token) {
      activateAccount(token);
    } else {
      setStatus('error');
      setMessage('Token de ativação não encontrado.');
    }
  }, [searchParams]);

  const activateAccount = async (token: string) => {
    try {
      const data = await apiFetch<{ message: string } | { success: false; error: string }>('/Authentication/ActivateAccount', {
        method: 'POST',
        body: JSON.stringify({ token }),
      });

      // Verificar se a resposta contém erro
      if ('success' in data && !data.success) {
        setStatus('error');
        setMessage(data.error || 'Erro ao ativar conta. Token pode estar expirado ou inválido.');
      } else {
        setStatus('success');
        setMessage('Conta ativada com sucesso! Redirecionando para login...');

        // Redirecionar para login após 2 segundos
        setTimeout(() => {
          navigate('/login', {
            state: {
              message: 'Conta ativada com sucesso! Faça login para continuar.'
            }
          });
        }, 2000);
      }
    } catch (error: any) {
      setStatus('error');
      setMessage(error.message || 'Erro de conexão. Tente novamente.');
    }
  };

  const resendActivationEmail = async () => {
    if (!email) {
      setMessage('Email não encontrado para reenvio.');
      return;
    }

    try {
      setStatus('loading');
      const data = await apiFetch<{ message: string } | { success: false; error: string }>('/Authentication/ResendActivationEmail', {
        method: 'POST',
        body: JSON.stringify({ email }),
      });

      if ('success' in data && !data.success) {
        setStatus('error');
        setMessage(data.error || 'Erro ao reenviar email de ativação.');
      } else {
        setStatus('success');
        setMessage('Email de ativação reenviado com sucesso! Verifique sua caixa de entrada.');
      }
    } catch (error: any) {
      setStatus('error');
      setMessage(error.message || 'Erro de conexão. Tente novamente.');
    }
  };

  const getIcon = () => {
    switch (status) {
      case 'success':
        return <CheckCircle sx={{ fontSize: 40 }} />;
      case 'error':
        return <Error sx={{ fontSize: 40 }} />;
      case 'loading':
        return <CircularProgress size={40} color="inherit" />;
      default:
        return <Email sx={{ fontSize: 40 }} />;
    }
  };

  const getTitle = () => {
    switch (status) {
      case 'success':
        return 'Conta Ativada!';
      case 'error':
        return 'Erro na Ativação';
      case 'loading':
        return 'Ativando Conta...';
      default:
        return 'Ativação de Conta';
    }
  };

  return (
    <Container>
      <Card>
        <IconContainer success={status === 'success'} error={status === 'error'}>
          {getIcon()}
        </IconContainer>

        <Typography variant="h4" component="h1" gutterBottom>
          {getTitle()}
        </Typography>

        <Typography variant="body1" color="textSecondary" paragraph>
          {message}
        </Typography>

        {status === 'error' && email && (
          <Box mt={3}>
            <Button
              variant="contained"
              color="primary"
              onClick={resendActivationEmail}
              startIcon={<Email />}
              fullWidth
            >
              Reenviar Email de Ativação
            </Button>
          </Box>
        )}

        {status === 'success' && !email && (
          <Box mt={3}>
            <Button
              variant="contained"
              color="primary"
              onClick={() => navigate('/login')}
              fullWidth
              size="large"
            >
              Ir para Login
            </Button>
          </Box>
        )}

        {status === 'error' && (
          <Box mt={2}>
            <Button
              variant="outlined"
              onClick={() => navigate('/login')}
              fullWidth
            >
              Voltar ao Login
            </Button>
          </Box>
        )}
      </Card>
    </Container>
  );
};

export default ActivateAccountPage;
